import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/designSystem/Select';
import { DeviceType } from '@/features/property/hooks/useScrapedPropertyWithFilters';
import { cn } from '@/utils';

interface FilterWrapperProps {
  children: React.ReactNode;
  className?: string;
}

export const FilterWrapper: React.FC<FilterWrapperProps> = ({ children, className }) => {
  return (
    <div className={cn("bg-gray-100 rounded-full px-3 py-1 text-sm", className)}>
      {children}
    </div>
  );
};

// Custom SelectTrigger for badge style
const BadgeSelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectTrigger>,
  React.ComponentPropsWithoutRef<typeof SelectTrigger>
>(({ className, children, ...props }, ref) => (
  <SelectTrigger
    ref={ref}
    className={cn(
      "h-auto w-auto border-none bg-transparent p-0 text-sm focus:ring-0 focus:ring-offset-0 [&>span]:line-clamp-1",
      className
    )}
    {...props}
  >
    {children}
  </SelectTrigger>
));

// Length of Stay Filter
interface LengthOfStayFilterProps {
  value: number;
  onChange: (value: number) => void;
  options: number[];
}

export const LengthOfStayFilter: React.FC<LengthOfStayFilterProps> = ({
  value,
  onChange,
  options
}) => {
  return (
    <FilterWrapper>
      <Select
        value={value.toString()}
        onValueChange={(val) => onChange(parseInt(val, 10))}
      >
        <BadgeSelectTrigger>
          <SelectValue />
        </BadgeSelectTrigger>
        <SelectContent>
          {options.map(option => (
            <SelectItem key={option} value={option.toString()}>
              {option} {option === 1 ? 'Night' : 'Nights'}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FilterWrapper>
  );
};

// Device Type Filter
interface DeviceTypeFilterProps {
  value: DeviceType;
  onChange: (value: DeviceType) => void;
}

export const DeviceTypeFilter: React.FC<DeviceTypeFilterProps> = ({
  value,
  onChange
}) => {
  return (
    <FilterWrapper>
      <Select value={value} onValueChange={onChange}>
        <BadgeSelectTrigger>
          <SelectValue />
        </BadgeSelectTrigger>
        <SelectContent>
          <SelectItem value="desktop">Desktop</SelectItem>
          <SelectItem value="mobile">Mobile</SelectItem>
        </SelectContent>
      </Select>
    </FilterWrapper>
  );
};

// Adults Filter
interface AdultsFilterProps {
  value: number;
  onChange: (value: number) => void;
  options: number[];
}

export const AdultsFilter: React.FC<AdultsFilterProps> = ({
  value,
  onChange,
  options
}) => {
  return (
    <FilterWrapper>
      <Select
        value={value.toString()}
        onValueChange={(val) => onChange(parseInt(val, 10))}
      >
        <BadgeSelectTrigger>
          <SelectValue />
        </BadgeSelectTrigger>
        <SelectContent>
          {options.map(option => (
            <SelectItem key={option} value={option.toString()}>
              {option} {option === 1 ? 'Adult' : 'Adults'}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FilterWrapper>
  );
};

// Rooms Filter
interface RoomsFilterProps {
  value: number;
  onChange: (value: number) => void;
  options: number[];
}

export const RoomsFilter: React.FC<RoomsFilterProps> = ({
  value,
  onChange,
  options
}) => {
  return (
    <FilterWrapper>
      <Select
        value={value.toString()}
        onValueChange={(val) => onChange(parseInt(val, 10))}
      >
        <BadgeSelectTrigger>
          <SelectValue />
        </BadgeSelectTrigger>
        <SelectContent>
          {options.map(option => (
            <SelectItem key={option} value={option.toString()}>
              {option} {option === 1 ? 'Room' : 'Rooms'}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FilterWrapper>
  );
};

// Days Filter
interface DaysFilterProps {
  value: number;
  onChange: (value: number) => void;
  options: number[];
}

export const DaysFilter: React.FC<DaysFilterProps> = ({
  value,
  onChange,
  options
}) => {
  return (
    <FilterWrapper>
      <Select
        value={value.toString()}
        onValueChange={(val) => onChange(parseInt(val, 10))}
      >
        <BadgeSelectTrigger>
          <SelectValue />
        </BadgeSelectTrigger>
        <SelectContent>
          {options.map(option => (
            <SelectItem key={option} value={option.toString()}>
              {option} {option === 1 ? 'Day' : 'Days'}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FilterWrapper>
  );
};

interface FilterContainerProps {
  children: React.ReactNode;
}

export const FilterContainer: React.FC<FilterContainerProps> = ({ children }) => {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {children}
    </div>
  );
};